import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Avatar, Chip, FormControlLabel, FormGroup, FormLabel, Typography } from '@mui/material';
import { setAgentOffline, setAgentOnline } from 'app/widget/agentService';
import { groupSvg } from 'common/assets/svgs';
import CustomSwitch from 'common/components/CustomSwitch';
import UserRole from 'common/enums/roles';
import { stringToColor } from 'common/utils/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUser } from 'redux/slices/authSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import {
    getAgentAvailable,
    getAgentForcedOffline,
    getAgentPresenceKnown,
    getMeetLoaded,
    getRealtimeAgents,
    setAgentPresence,
} from 'redux/slices/realtimeSlice';
import { presenceOf, shouldSwitchOn } from './agentPresence';
import useStylesUserInfo from './styles/useStylesUserInfo.styles';

const UserInfo = (props) => {
    const { onNavigate } = props;

    const { t } = useTranslation();
    const { classes, cx } = useStylesUserInfo();
    const user = useSelector(getUser);
    const available = useSelector(getAgentAvailable);
    const forcedOffline = useSelector(getAgentForcedOffline);
    const presenceKnown = useSelector(getAgentPresenceKnown);
    const realtimeAgents = useSelector(getRealtimeAgents);
    const [actionPopup, setActionPopup] = useState(false);
    const [agentWaitTimeout, setAgentWaitTimeout] = useState(null);
    const dispatch = useDispatch();
    const [scheduleHasAgentCurrentEvent, setScheduleHasAgentCurrentEvent] = useState(false);
    const [loading, setLoading] = useState(false);
    const meetLoaded = useSelector(getMeetLoaded);
    const navigate = useNavigate();
    const [hasInteraction, setHasInteraction] = useState(false);
    const organization = useSelector(getOrganization);

    const agent = useMemo(() => {
        const { role, ...agent } = user;
        return {
            ...agent,
            id: user?.userId,
        };
    }, [user]);

    const role = useMemo(() => {
        return user?.role;
    }, [user?.role]);

    const goToProfile = () => {
        if (onNavigate) {
            onNavigate();
        }
        navigate('/my-profile');
    };

    const handleCheckChange = async () => {
        if (loading) return;
        setLoading(true);
        try {
            if (!available) {
                await setAgentOnline(agent);
            } else {
                // Unavailable is the agent's choice and persists in Firebase (forcedOffline), where the
                // app and the chat service read it: the last one to set it wins.
                await setAgentOffline(agent.organizationId, agent.id, true);
            }
        } catch (err) {
            console.error('Failed to toggle agent availability:', err);
        } finally {
            setLoading(false);
        }
    };

    const observeAgentCallback = useCallback(
        (event) => {
            dispatch(setAgentPresence(presenceOf(event)));
        },
        [dispatch],
    );

    // Firebase is the only memory of the agent's choice, wherever it was made (the app too): the
    // switch follows it from the moment it exists, not from the first click.
    useEffect(() => {
        if (!realtimeAgents || !agent.id) return undefined;
        realtimeAgents.stopObserverAgentWithCallback(agent.id, observeAgentCallback);
        realtimeAgents.observeAgent(agent.id, observeAgentCallback);
        return () => realtimeAgents.stopObserverAgentWithCallback(agent.id, observeAgentCallback);
    }, [realtimeAgents, agent.id, observeAgentCallback]);

    const switchOn = shouldSwitchOn({ role, presenceKnown, forcedOffline });
    const switchOnRef = useRef(switchOn);
    switchOnRef.current = switchOn;

    useEffect(() => {
        if (scheduleHasAgentCurrentEvent) {
            const hasAgentCurrentEventTimeout = setTimeout(() => {
                setScheduleHasAgentCurrentEvent(false);
            }, 2000);
            return () => clearTimeout(hasAgentCurrentEventTimeout);
        }
    }, [scheduleHasAgentCurrentEvent, realtimeAgents, agent?.id, agent?.organizationId]);

    const clickAction = useCallback(() => {
        if (switchOnRef.current) {
            setAgentOnline(agent).catch((err) => console.error('Failed to set agent online:', err));
        }
        setScheduleHasAgentCurrentEvent(true);
        if (agentWaitTimeout) {
            clearTimeout(agentWaitTimeout);
        }
        document.body.removeEventListener('click', clickAction);
        window['onlive_agent_click'] = true;
    }, [agent, agentWaitTimeout]);

    useEffect(() => {
        if (realtimeAgents) {
            if (!hasInteraction) {
                setHasInteraction(true);
                const agentActionTimeout = setTimeout(() => {
                    if (switchOnRef.current) {
                        if (!realtimeAgents?.hasAgentCurrentEvent(agent?.id) && !available) {
                            setActionPopup(true);
                        } else {
                            setAgentOnline(agent).catch((err) => console.error('Failed to set agent online:', err));
                        }
                    }
                }, 1000);
                setAgentWaitTimeout(agentActionTimeout);
                return () => clearTimeout(agentActionTimeout);
            } else if (!available && switchOn) {
                setAgentOnline(agent).catch((err) => console.error('Failed to set agent online:', err));
            }
        }
    }, [realtimeAgents, switchOn, available, agent, hasInteraction]);

    useEffect(() => {
        if (realtimeAgents) {
            document.body.addEventListener('click', clickAction);
        }
        return () => document.body.removeEventListener('click', clickAction);
    }, [realtimeAgents, clickAction]);

    return (
        <>
            <div className={classes.userProfile}>
                {role === 'root' ? (
                    <div className={classes.adminIcon}>
                        <SettingsIcon />
                    </div>
                ) : (
                    <Avatar alt={user?.name} src={user?.avatar} size="small" />
                )}
                <div aria-hidden={true} className={classes.userInfo} onClick={goToProfile}>
                    <span>{user?.name}</span>
                    {role === UserRole.ROOT && (
                        <Chip
                            style={{
                                backgroundColor: stringToColor(organization.id),
                                height: '18px',
                            }}
                            size="small"
                            label={
                                <Typography variant="body1" fontWeight={500}>
                                    {organization.name}
                                </Typography>
                            }
                        />
                    )}
                    <div className={classes.userRole}>
                        <VerifiedUserIcon />
                        <span>{t(`roles.role.${role}`)}</span>
                    </div>
                </div>

                {realtimeAgents && meetLoaded && (
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <CustomSwitch
                                    checked={available ?? false}
                                    onChange={handleCheckChange}
                                    name="available"
                                    color="primary"
                                />
                            }
                            label={
                                <FormLabel className={classes.availableControl}>
                                    {available
                                        ? t('components.userInfo.available')
                                        : t('components.userInfo.notAvailable')}
                                </FormLabel>
                            }
                        />
                    </FormGroup>
                )}
            </div>
            {window.self === window.top && (
                <div
                    aria-hidden={true}
                    className={cx(classes.notificationsPopupBackground, {
                        [classes.notificationsPopupHidden]: !actionPopup,
                    })}
                    onClick={() => {
                        clickAction();
                        window['onlive_agent_click'] = true;
                        setActionPopup(false);
                    }}
                >
                    <div className={classes.notificationContent}>
                        {groupSvg}
                        <h3>{t('components.userInfo.afk')}</h3>
                        <p>{t('components.userInfo.pleaseClick')}</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserInfo;
